import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Package, MapPin, User, Save, Plus, Trash2, Edit2, Shield, AlertCircle, X } from 'lucide-react';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('packages');
  const [packages, setPackages] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Form States
  const [newPackage, setNewPackage] = useState({ name: '', speed: '', monthly_price: '' });
  const [newArea, setNewArea] = useState({ name: '' });
  const [editingPackage, setEditingPackage] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pkgRes, areaRes] = await Promise.all([
        api.get('/packages/'),
        api.get('/areas/')
      ]);
      setPackages(pkgRes.data.results || []);
      setAreas(areaRes.data.results || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddPackage = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/packages/', newPackage);
      setNewPackage({ name: '', speed: '', monthly_price: '' });
      fetchData();
    } catch (err) {
      alert("Failed to add package");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePackage = async (e) => {
    e.preventDefault();
    if (!editingPackage) return;
    setLoading(true);
    try {
      await api.put(`/packages/${editingPackage.id}/`, editingPackage);
      setEditingPackage(null);
      fetchData();
    } catch (err) {
      alert("Failed to update package");
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePackage = async (id) => {
    if (!window.confirm("Are you sure? Ensure no active customers are using this package.")) return;
    try {
      await api.delete(`/packages/${id}/`);
      fetchData();
    } catch (err) {
      alert("Failed to delete package");
    }
  };

  const handleAddArea = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/areas/', newArea);
      setNewArea({ name: '' });
      fetchData();
    } catch (err) {
      alert("Failed to add area");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteArea = async (id) => {
    if (!window.confirm("Are you sure? Ensure no customers are assigned to this area.")) return;
    try {
      await api.delete(`/areas/${id}/`);
      fetchData();
    } catch (err) {
      alert("Failed to delete area");
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
      <div className="mb-8">
        <h2 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
          <span className="w-1 h-6 bg-blue-600 rounded-full"></span> System Settings
        </h2>
        <p className="text-sm text-gray-500">Manage packages, areas, and system configuration</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-200 mb-6">
        {[
          { id: 'packages', label: 'Internet Packages', icon: Package },
          { id: 'areas', label: 'Service Areas', icon: MapPin },
          { id: 'profile', label: 'Admin Profile', icon: User },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 pb-3 px-2 text-sm font-medium transition-all ${
              activeTab === tab.id 
                ? 'border-b-2 border-blue-600 text-blue-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon size={16} /> {tab.label}
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 min-h-[400px]">
        
        {/* 🔥 Packages Tab */}
        {activeTab === 'packages' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Plus size={18} className="text-blue-600" /> Add New Package
              </h3>
              <form onSubmit={handleAddPackage} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase">Package Name</label>
                  <input 
                    type="text" required placeholder="e.g., Gold Fiber" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    value={newPackage.name}
                    onChange={(e) => setNewPackage({...newPackage, name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase">Speed</label>
                  <input 
                    type="text" required placeholder="e.g., 10 Mbps" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    value={newPackage.speed}
                    onChange={(e) => setNewPackage({...newPackage, speed: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase">Monthly Price (PKR)</label>
                  <input 
                    type="number" required placeholder="1500" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    value={newPackage.monthly_price}
                    onChange={(e) => setNewPackage({...newPackage, monthly_price: e.target.value})}
                  />
                </div>
                <button 
                  type="submit" disabled={loading}
                  className="w-full bg-blue-600 text-white font-bold py-2.5 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Save size={18} /> Save Package
                </button>
              </form>
            </div>

            <div className="lg:col-span-2">
              <h3 className="font-bold text-gray-800 mb-4">Existing Packages</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                    <tr>
                      <th className="p-3">Name</th>
                      <th className="p-3">Speed</th>
                      <th className="p-3">Price</th>
                      <th className="p-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {packages.map(pkg => (
                      <tr key={pkg.id} className={editingPackage?.id === pkg.id ? "bg-blue-50" : ""}>
                        {editingPackage?.id === pkg.id ? (
                          // 🔥 Edit Mode Row
                          <>
                            <td className="p-3">
                              <input 
                                className="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none" 
                                value={editingPackage.name}
                                onChange={(e) => setEditingPackage({...editingPackage, name: e.target.value})}
                              />
                            </td>
                            <td className="p-3">
                              <input 
                                className="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none" 
                                value={editingPackage.speed}
                                onChange={(e) => setEditingPackage({...editingPackage, speed: e.target.value})}
                              />
                            </td>
                            <td className="p-3">
                              <input 
                                type="number"
                                className="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none" 
                                value={editingPackage.monthly_price}
                                onChange={(e) => setEditingPackage({...editingPackage, monthly_price: e.target.value})}
                              />
                            </td>
                            <td className="p-3 text-right flex justify-end gap-2">
                              <button onClick={handleUpdatePackage} className="text-green-600 hover:bg-green-100 p-1.5 rounded transition-colors"><Save size={16}/></button>
                              <button onClick={() => setEditingPackage(null)} className="text-gray-500 hover:bg-gray-100 p-1.5 rounded transition-colors"><X size={16}/></button>
                            </td>
                          </>
                        ) : (
                          // 🔥 View Mode Row
                          <>
                            <td className="p-3 font-medium">{pkg.name}</td>
                            <td className="p-3 text-sm text-gray-600">{pkg.speed}</td>
                            <td className="p-3 font-bold">PKR {pkg.monthly_price}</td>
                            <td className="p-3 text-right flex justify-end gap-2">
                              <button onClick={() => setEditingPackage(pkg)} className="text-blue-500 hover:bg-blue-50 p-2 rounded-lg transition-colors">
                                <Edit2 size={16} />
                              </button>
                              <button onClick={() => handleDeletePackage(pkg.id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition-colors">
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* 🔥 Important Note for Admin */}
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-sm text-yellow-800 flex gap-3 items-start">
                <AlertCircle size={20} className="flex-shrink-0 mt-0.5 text-yellow-600" />
                <p>
                  <strong>Note:</strong> Changing the price here will affect <strong>future invoices</strong> generated for this package. 
                  Existing unpaid invoices will retain their original amount unless manually updated.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 🔥 Areas Tab */}
        {activeTab === 'areas' && (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
    <div className="lg:col-span-1">
      <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
        <Plus size={18} className="text-green-600" /> Add New Area
      </h3>
      <form onSubmit={handleAddArea} className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase">Area Name</label>
          <input 
            type="text" required placeholder="e.g., Jalalpur Jattan" 
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
            value={newArea.name}
            onChange={(e) => setNewArea({...newArea, name: e.target.value})}
          />
        </div>
        <button 
          type="submit" disabled={loading}
          className="w-full bg-green-600 text-white font-bold py-2.5 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
        >
          <Save size={18} /> Save Area
        </button>
      </form>
    </div>

    <div className="lg:col-span-2">
      <h3 className="font-bold text-gray-800 mb-4">Service Areas & Stats</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {areas.map(area => (
          <div key={area.id} className="p-5 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all group relative">
            <div className="flex justify-between items-start mb-4">
              <h4 className="font-bold text-lg text-gray-900">{area.name}</h4>
              <button 
                onClick={() => handleDeleteArea(area.id)} 
                className="text-gray-300 hover:text-red-500 transition-colors p-1"
              >
                <Trash2 size={18} />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-green-50 p-3 rounded-lg border border-green-100">
                <p className="text-xs text-green-600 font-bold uppercase mb-1">Active</p>
                <p className="text-2xl font-bold text-green-700">{area.active_customers}</p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg border border-red-100">
                <p className="text-xs text-red-600 font-bold uppercase mb-1">Expired</p>
                <p className="text-2xl font-bold text-red-700">{area.expired_customers}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
)}

        {/* 🔥 Profile Tab */}
        {activeTab === 'profile' && (
          <div className="max-w-2xl">
            <h3 className="font-bold text-gray-800 mb-6 flex items-center gap-2">
              <Shield size={18} className="text-purple-600" /> Admin Security Settings
            </h3>
            <div className="p-8 bg-gray-50 rounded-xl border border-dashed border-gray-300 text-center">
              <User size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">Profile management and password changes are handled via the main Profile page.</p>
              <a href="/profile" className="text-blue-600 font-bold hover:underline">Go to My Profile &rarr;</a>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Settings;


